import { useState } from "react";
import styles from "./InfinityToggle.module.css";

interface Props {
  /**
   * What the field currently holds. `0` is the value this toggle writes, so
   * `0` is the toggle being ON — there is no second copy of the state to keep
   * in step with the config.
   */
  value: number | undefined;
  /**
   * Write the field. `0` when the toggle goes on; the number the box held
   * before, or `fallback`, or `undefined` when there is neither, when it goes
   * off.
   */
  onChange: (next: number | undefined) => void;
  /**
   * What switching OFF restores when the operator never typed a number in this
   * box — a project that arrived holding `0` has nothing to go back to. Omit it
   * where an empty box is itself a state: a row's `ttl` then returns to "use
   * the section default".
   */
  fallback?: number;
  /**
   * The field this toggle belongs to, in the words its own label uses. It is
   * the only thing a screen reader has to tell three identical ∞ buttons apart.
   */
  fieldLabel: string;
  /**
   * What ∞ means for this field, as the tail of the button's name and its
   * tooltip. A duration never expires; the end of an invite ladder's step
   * "has no end", and saying it expires would describe a different setting.
   */
  meaning?: string;
  /** Follows the field: a box the operator may not edit must not offer a
   *  button that edits it. */
  disabled?: boolean;
  className?: string;
}

/**
 * The ∞ ("never expires") toggle that rides inside a duration box, hard against
 * its right edge.
 *
 * Mockup: .codelane/artifacts/t-104/config-help-infinite-ttl.html L107-L126 —
 * the one piece of invented geometry in that file. ONE control for all three
 * durations of the Help requests section (`unclaimed_ttl_seconds`,
 * `default_ttl_seconds` and a row's `ttl_seconds`), so "forever" is spelt one
 * way in the card and in the types grid alike.
 *
 * It is positioned absolutely: the carrier supplies a `position: relative` box
 * around its input, which is what makes the same button sit correctly in a
 * 168px field and in a 160px grid cell.
 *
 * ON writes `0`, which is the very number the backend already reads as "never"
 * for all three keys. The `min=60` rule stays on the NUMBER — ∞ is a mode, not
 * a value typed into the box — so the save bar never has to make an exception
 * for a zero somebody could have typed.
 */
export function InfinityToggle({
  value,
  onChange,
  fallback,
  fieldLabel,
  meaning = "never expires",
  disabled,
  className = "",
}: Props) {
  const never = value === 0;
  /**
   * The number to come back to, remembered the moment the field STOPS holding
   * one — which is not only when this button is pressed. A box can reach `0`
   * by typing: deleting digits off the left of `600` passes through `00`, and
   * the field locks into «Never» in the middle of an edit. Watching the value
   * itself covers both ways in, so the way out gives the same number back.
   *
   * Mirroring the prop into state is React's own answer for adjusting state
   * during render; the two alternatives are both refused by this project's
   * lint for good reason — a ref written during render is a desync under
   * StrictMode, and an effect is a second render pass. `Object.is` rather than
   * `!==` because an empty box is NaN, which is never equal to itself and
   * would re-enter this branch on every render for ever.
   *
   * The memory lasts as long as this button does. Whatever unmounts the field
   * — the config page switching to its Raw JSON tab, a row removed above this
   * one — forgets it, and switching ∞ off then falls back instead. The cost is
   * one number to retype; the alternative is keeping it beside the config
   * itself, which is a second place obliged to agree with the value.
   */
  const [last, setLast] = useState<number | undefined>(undefined);
  const [prev, setPrev] = useState(value);
  if (!Object.is(value, prev)) {
    setPrev(value);
    // A blank or half-typed box leaves the memory alone: an older number is a
    // better thing to hand back than nothing at all.
    if (typeof prev === "number" && Number.isFinite(prev) && prev > 0) {
      setLast(prev);
    }
  }

  function toggle() {
    onChange(never ? (last ?? fallback) : 0);
  }

  return (
    <button
      type="button"
      className={[styles.btn, never ? styles.on : "", className]
        .filter(Boolean)
        .join(" ")}
      onClick={toggle}
      disabled={disabled}
      aria-pressed={never}
      title={meaning.charAt(0).toUpperCase() + meaning.slice(1)}
      aria-label={`${fieldLabel} ${meaning}`}
    >
      ∞
    </button>
  );
}
